package com.eladfinish.bible.contest.screens.browse.contentOfChapter

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.Stable
import com.eladfinish.bible.contest.arch.ActionKt
import com.eladfinish.bible.contest.arch.EffectKt
import com.eladfinish.bible.contest.arch.StateKt
import com.eladfinish.bible.contest.data.ChapterData
import com.eladfinish.bible.contest.data.common.DocumentTextType
import com.eladfinish.bible.contest.features.copyOptions.model.CopyOptionsOperation
import com.eladfinish.bible.contest.navigation.NavigationCommand
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.model.ContentMode
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.CompleteType
import com.eladfinish.bible.contest.text.ClickAction
import com.eladfinish.bible.contest.ui.menus.actions.ChapterContentToolbarAction
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.ImmutableMap

@Immutable
data class BrowseChaptersState(
    val isCompleteChapter: Boolean,
    val isDebugMode: Boolean,
    val textSize: Float,
    val numberOfParts: Int,
    val currentBook: Int,
    val currentPart: Int,
    val bookName: String,
    val previousBookName: String,
    val nextBookName: String,
    val tabs: ImmutableList<PartTabSpec>,
    val parts: ImmutableMap<Int, PartUiState>,
    val dialogState: DialogState,
    val isLoading: Boolean,
    /** The page that keeps the button to enter, or change, the name Tehilim 119 spells; null when none does. */
    val deceasedNamePage: Int? = null,
) : StateKt {
    @Stable
    sealed class PartUiState {
        data object Idle : PartUiState()
        data object Loading : PartUiState()
        data class Ready(
            val chapterData: ChapterData,
            val stamFont: String? = null,
            val contentMode: ContentMode = ContentMode.Normal,
            val scrollEvent: ScrollEvent = ScrollEvent.None,
        ) : PartUiState()

        data class Error(val message: String) : PartUiState()

        /** Tehilim 119 of the "שונות" tab, until the name its verses spell is entered. */
        data object AwaitingDeceasedName : PartUiState()
    }

    @Stable
    sealed interface PartTabSpec {
        data object ArrowRight : PartTabSpec
        data class Part(val stringDesc: StringDesc) : PartTabSpec
        data object ArrowLeft : PartTabSpec
    }

    @Stable
    sealed class DialogState {
        data class BrowseCopyOptions(
            val operation: CopyOptionsOperation,
            val title: StringResource,
            val text: String,
            val bookAndChapterForExport: StringDesc,
            val shouldAddParenthesisOnOtiotPasuk: Boolean,
            val documentTextType: DocumentTextType,
        ) : DialogState()

        data class FontSize(
            val currentFontSize: Float,
        ) : DialogState()

        data class ChooseAliyah(
            val title: StringDesc,
        ) : DialogState()

        /** [currentName] prefills the field: empty until a name is entered. */
        data class DeceasedName(val currentName: String = "") : DialogState()

        data object None : DialogState()
    }
}

sealed interface BrowseChaptersAction : ActionKt {
    data class ToolbarClicked(val toolbarAction: ChapterContentToolbarAction) : BrowseChaptersAction

    data object NavigateToPreviousBook : BrowseChaptersAction
    data object NavigateToNextBook : BrowseChaptersAction

    // fired when a chapter page becomes the settled page (not while scrolling)
    data class PartSettled(
        val bookIndex: Int,     // 1-based
        val partIndex: Int,     // 1-based
    ) : BrowseChaptersAction

    // keep your existing load trigger if you want
    data class PageVisible(
        val bookIndex: Int,
        val partIndex: Int,
    ) : BrowseChaptersAction

    data class UpdateFontSize(val value: Float) : BrowseChaptersAction
    data class AliyahClicked(val aliyahLabel: String) : BrowseChaptersAction
    data class ExposureTypesClicked(val completeType: CompleteType) : BrowseChaptersAction
    data object HideDialog : BrowseChaptersAction

    data object EnterDeceasedNameClicked : BrowseChaptersAction
    data class DeceasedNameConfirmed(val name: String) : BrowseChaptersAction

    data class ClipboardTextCopied(val rawText: String) : BrowseChaptersAction

    /**
     * User requested copy from the selection menu.
     * Keep rawSelectedText as-is; formatting/cleanup decisions belong to business layer.
     */
    data class CopySelectedText(val rawSelectedText: String) : BrowseChaptersAction

    /**
     * User requested search from the selection menu.
     * We send raw text and let domain layer validate / normalize.
     */
    data class SearchSelectedText(val rawSelectedText: String) : BrowseChaptersAction

    data class SaveFile(
        val content: String,
        val bookAndChapterForExport: String,
        val documentTextType: DocumentTextType,
    ) : BrowseChaptersAction

    data class ChapterCompleteClick(
        val clickAction: ClickAction,
    ) : BrowseChaptersAction

    data object ConsumeScrollEvent : BrowseChaptersAction
}

sealed interface BrowseChaptersEffect : EffectKt {
    data class ScrollToAliyah(val aliyahLabel: String) : BrowseChaptersEffect
    /** [textOffset] wins when known (not -1): a bookmark from before it existed only has the pixels. */
    data class ScrollToPosition(val yOffest: Int, val textOffset: Int = -1) : BrowseChaptersEffect
    data class ShowToast(val text: String) : BrowseChaptersEffect
    data class ShowToastStringResource(val resource: StringResource) : BrowseChaptersEffect

    data object NotifyBookmarkSaved : BrowseChaptersEffect

    data class OpenContent(
        val navigation: NavigationCommand.Navigate,
    ) : BrowseChaptersEffect
}

sealed interface ScrollEvent {
    data object None : ScrollEvent

    data object ToTop : ScrollEvent

    data object ToBottom : ScrollEvent
}
